import { Alert,Box, CircularProgress } from '@mui/material';
import { Buffer } from 'buffer';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fetchPreviewContext } from '../api/ventura/preview';
import { AmbientUtilityGate } from '../components/PreviewStudio/AmbientUtilityGate';
import PreviewStudioLayout from '../components/PreviewStudio/PreviewStudioLayout';
import { useConfig } from '../hooks/useConfig';
import { PreviewContext } from '../types/previewStudio';
import posthog from '../util/posthog';

/**
 * Preview Studio Page
 * 
 * Main entry point for the Preview Studio feature.
 * Supports deep linking via URL params:
 * - ?campaign_id=123&creative_id=456 (both) - Scenario A
 * - ?campaign_id=123 (campaign only) - Scenario A
 * - ?creative_id=456 (creative only) - Scenario B
 */
export default function PreviewStudioPage() {
  const [searchParams] = useSearchParams();
  const { apiHost } = useConfig();
  const [context, setContext] = useState<PreviewContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse URL params
  const campaignIdParam = searchParams.get('campaign_id');
  const creativeIdParam = searchParams.get('creative_id');
  const rawTagParam = searchParams.get('raw_tag');
  const tagHtmlParam = searchParams.get('tag_html');

  useEffect(() => {
    async function loadContext() {
      // If raw tag mode, skip context loading
      if (rawTagParam === 'true') {
        setLoading(false);
        return;
      }

      // If no params, show empty state
      if (!campaignIdParam && !creativeIdParam) {
        setLoading(false);
        return;
      }

      // Load context for both campaign and creative-only modes
      // Backend returns different modes based on what's provided
      try {
        setLoading(true);
        setError(null);

        const params: { campaign_id?: number; creative_id?: number } = {};
        
        if (campaignIdParam) {
          params.campaign_id = parseInt(campaignIdParam, 10);
        }
        
        if (creativeIdParam) {
          params.creative_id = parseInt(creativeIdParam, 10);
        }

        const contextData = await fetchPreviewContext(apiHost, params);
        setContext(contextData);
        posthog.capture('preview_studio_opened', {
          campaign_id: campaignIdParam ? Number(campaignIdParam) : undefined,
          creative_id: creativeIdParam ? Number(creativeIdParam) : undefined,
          mode: campaignIdParam ? 'campaign' : 'creative',
        });
      } catch (err) {
        posthog.captureException(err instanceof Error ? err : new Error('preview_studio_load_failed'));
        setError(err instanceof Error ? err.message : 'Failed to load preview context');
      } finally {
        setLoading(false);
      }
    }

    loadContext();
  }, [campaignIdParam, creativeIdParam, rawTagParam, apiHost]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Box sx={{ color: 'text.secondary' }}>Loading preview context...</Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Main content
  return (
    <>
      <PreviewStudioLayout
        initialContext={context}
        initialRawTag={tagHtmlParam ? Buffer.from(tagHtmlParam, 'base64').toString('utf-8') : undefined}
      />
      {/* Easter egg: utility appears after extended use */}
      <AmbientUtilityGate />
    </>
  );
}
